import { computed, Ref, ref } from 'vue';
import { type StoreGeneric, storeToRefs } from 'pinia';
import type {
  Identity,
  MemberType,
  ContentCollection,
  ShareableContentCollection,
} from '@ilteducation/content-collections-types';
import useContentCollectionsStore from './content-collections';
import { AddMemberParams, ContentReference, ContentsByReferences, RemoveMemberParams } from './types';
import {
  addContent as addContentApi,
  addMember as addMemberApi,
  reorderContent as reorderContentApi,
  removeContent as removeContentApi,
  removeMember as removeMemberApi,
  subscribeToCollection as subscribeApi,
  unsubscribeFromCollection as unsubscribeApi,
  removeHistoryContent as removeHistoryContentApi,
} from './api';
import useMyCollectionsStore from './my-collections';
import useCollectionContentsStore from './collection-contents';
import {
  addContentsReferences,
  isCollectionCreator,
  isCollectionMember,
  isCollectionOwner,
  isCollectionSubscriber,
  isFullProjection,
  removeContentsReferences,
  isHistoryCollection,
  isPredefinedCollection,
  isShareRelatedCollection,
} from './utils';
import { useMyProfiles } from '@ilteducation/profile';
import { useUserContext } from '@ilteducation/ilt-auth';

const useContentCollection = (collectionId: Ref<string>) => {
  const { collectionById, isLoadingById, load, put, errorById } = useContentCollectionsStore();
  const { selectedContents } = storeToRefs(useContentCollectionsStore());
  const { update } = useMyCollectionsStore();
  const { moveContent, dropContents } = useCollectionContentsStore();

  const { user, realm } = storeToRefs(useUserContext() as unknown as StoreGeneric);
  const { current: currentProfile } = useMyProfiles();
  const identity = computed<Identity>(() => ({
    userId: user.value.uid,
    profileId: currentProfile.value?.id,
  }));

  const isUpdating = ref(false);
  const error = computed(() => errorById(collectionId));
  const isLoading = computed(() => isUpdating.value || isLoadingById(collectionId));
  /**
   * `getContentCollection` turns a 404 into `null` rather than an error, and the store writes that
   * null as the entry, so a missing id leaves both `collection` and `error` empty. Undefined still
   * means "not loaded yet".
   */
  const notFound = computed(
    () => !isLoading.value && (collectionById(collectionId) === null || error.value?.status === 404),
  );
  const collection = computed<ShareableContentCollection | undefined>(() => {
    const c = collectionById(collectionId);
    if (c && isFullProjection(c)) {
      return c;
    }

    if (c && (!isCollectionOwner(c) || collectionId.value === 'history')) {
      // allow the virtual own shelf history as well...
      // simply dummy-patch here if user isn't an owner, instead of fetching from remote
      const patchedCollection: ContentCollection = {
        contents: {
          type: 'ContentsByReferences',
          references: [],
        },
        realm: realm.value,
        ...c,
      };
      return patchedCollection;
    }

    return undefined;
  });

  const addMember = (params: AddMemberParams): Promise<void> => {
    isUpdating.value = true;

    return addMemberApi(collectionId.value, params)
      .then(() => {
        const patch = { members: [...collection.value!.members!, params] };

        put([{ ...collection.value!, ...patch }]);
      })
      .finally(() => {
        isUpdating.value = false;
      });
  };

  const removeMember = (params: RemoveMemberParams): Promise<void> => {
    isUpdating.value = true;

    return removeMemberApi(collectionId.value, params)
      .then(() => {
        const patch = {
          members: collection.value!.members!.filter(
            (member) => member.type === params.type && member.id === params.id,
          ),
        };

        put([{ ...collection.value!, ...patch }]);
      })
      .finally(() => {
        isUpdating.value = false;
      });
  };

  const addContents = (contentReferences: ContentReference[]): Promise<void> => {
    if (collection.value?.contents.type !== 'ContentsByReferences') {
      console.warn(
        `Can't add content to collection ${collectionId.value} with contents ${collection.value?.contents?.type}`,
      );
      return Promise.resolve();
    }

    const contents = addContentsReferences(collection.value.contents, contentReferences);

    isUpdating.value = true;
    if (contentReferences.length === 1) {
      return addContentApi(collectionId.value, contentReferences[0], currentProfile.value?.id)
        .then(() => put([{ ...collection.value!, contents }]))
        .finally(() => {
          isUpdating.value = false;
        });
    }

    return update(collectionId.value, { contents }).finally(() => {
      isUpdating.value = false;
    });
  };

  const updateContents = (contentReferences: ContentReference[]): Promise<void> => {
    if (collection.value?.contents.type !== 'ContentsByReferences') {
      console.warn(
        `Can't update contents to collection ${collectionId.value} with contents ${collection.value?.contents?.type}`,
      );
      return Promise.resolve();
    }

    isUpdating.value = true;

    return update(collectionId.value, {
      contents: {
        type: 'ContentsByReferences',
        references: contentReferences,
      },
    }).finally(() => {
      isUpdating.value = false;
    });
  };

  const reorderContent = (movedContentId: string, afterContentId?: string): Promise<void> => {
    if (collection.value?.contents.type !== 'ContentsByReferences') {
      console.warn(
        `Can't reorder content in collection ${collectionId.value} with contents ${collection.value?.contents?.type}`,
      );
      return Promise.resolve();
    }

    isUpdating.value = true;

    return reorderContentApi(collectionId.value, movedContentId, afterContentId)
      .then((updateCollection) => {
        // Reordering keeps the loaded contents in order itself, so the cache must not be reset here:
        // that dropped every loaded page mid-sort and put a spinner over the list.
        put([updateCollection], false);
        moveContent(collectionId.value, movedContentId, afterContentId);
      })
      .finally(() => {
        isUpdating.value = false;
      });
  };

  const removeContents = (contentReferences: ContentReference[]): Promise<void> => {
    if (collection.value?.contents.type !== 'ContentsByReferences') {
      console.warn(
        `Can't add content to collection ${collectionId.value} with contents ${collection.value?.contents?.type}`,
      );
      return Promise.resolve();
    }

    const contents = removeContentsReferences(collection.value.contents, contentReferences);
    isUpdating.value = true;

    // The loaded pages are the only place a history or predefined collection holds its items.
    const forgetRemoved = () =>
      dropContents(
        collectionId.value,
        contentReferences.map(({ id }) => id),
      );

    if (isHistoryCollection(collectionId.value)) {
      if (contentReferences.length === 1) {
        return removeHistoryContentApi(contentReferences[0], currentProfile.value?.id)
          .then(() => {
            forgetRemoved();
            // The cache is kept in step by forgetRemoved, so put must not reset and refetch it.
            put([{ ...collection.value!, contents }], false);
          })
          .finally(() => {
            isUpdating.value = false;
          });
      }
      return Promise.all(
        contentReferences.map((contentReference) =>
          removeHistoryContentApi(contentReference, currentProfile.value?.id),
        ),
      )
        .then(() => {
          forgetRemoved();
          put([{ ...collection.value!, contents }], false);
        })
        .finally(() => {
          isUpdating.value = false;
        });
    }

    if (contentReferences.length === 1) {
      return removeContentApi(collectionId.value, contentReferences[0], currentProfile.value?.id)
        .then(() => {
          forgetRemoved();
          put([{ ...collection.value!, contents }], false);
        })
        .finally(() => {
          isUpdating.value = false;
        });
    }

    return update(collectionId.value, { contents })
      .then(forgetRemoved)
      .finally(() => {
        isUpdating.value = false;
      });
  };

  const updateOwners = async (owners: Identity[]): Promise<void> => {
    isUpdating.value = true;
    return update(collectionId.value, { owners }).finally(() => {
      isUpdating.value = false;
    });
  };

  const subscribe = async (): Promise<void> => {
    if (isUpdating.value) {
      return Promise.resolve();
    }
    isUpdating.value = true;
    return subscribeApi(collectionId.value).then(() => {
      if (!collection.value) {
        isUpdating.value = false;
        return;
      }
      if (!collection.value.subscribers) {
        collection.value.subscribers = [];
      }
      collection.value.subscribers.push({ id: identity.value.userId, type: 'user' as MemberType.User });
      isUpdating.value = false;
    });
  };

  const unsubscribe = async (): Promise<void> => {
    if (isUpdating.value || !collection.value || !collection.value.subscribers) {
      return Promise.resolve();
    }
    isUpdating.value = true;
    return unsubscribeApi(collectionId.value).then(() => {
      if (!collection.value || !collection.value.subscribers) {
        isUpdating.value = false;
        return;
      }
      isUpdating.value = false;
      collection.value.subscribers = collection.value.subscribers.filter((s) => s.id !== identity.value.userId);
    });
  };

  const contentReferences = computed(
    () => (collection.value?.contents as ContentsByReferences | undefined)?.references ?? [],
  );

  const hasContent = computed(
    () => (contentReference: ContentReference) =>
      contentReferences.value.some((cr) => cr.type === contentReference.type && cr.id === contentReference.id),
  );

  if (!collection.value && !isLoading.value) {
    load(collectionId.value);
  }
  const isCreator = computed(() => collection.value && isCollectionCreator(collection.value));

  const isOwner = computed(() => collection.value && isCollectionOwner(collection.value));

  const isSubscriber = computed(() => collection.value && isCollectionSubscriber(collection.value));

  const isMember = computed(() => collection.value && isCollectionMember(collection.value));

  const isCuratedCollection = computed(() => collection.value && collection.value?.owners === undefined);

  const isPredefined = computed(() => collection.value && isPredefinedCollection(collectionId));

  const isShareRelated = computed(() => collection.value && isShareRelatedCollection(collectionId));

  return {
    collection,
    isLoading,
    hasContent,
    selectedContents,
    addMember,
    updateOwners,
    removeMember,
    addContents,
    updateContents,
    reorderContent,
    removeContents,
    load,
    subscribe,
    unsubscribe,
    error,
    notFound,
    isCreator,
    isOwner,
    isMember,
    isSubscriber,
    isCuratedCollection,
    isPredefined,
    isShareRelated,
  };
};

export default useContentCollection;
